import { useQuery } from '@tanstack/react-query';

import {
  FlightListViewModel,
  AirportSearchViewModel,
  FlightNodeViewModel,
} from '@api/index';
import { callService } from '@api/caller';

import { LIST_FLIGHTS_PAGE_SIZE } from '../constants';

type AirportSearchType = {
  airportName: string | undefined;
  select?: (data: AirportSearchViewModel[]) => AirportSearchViewModel[];
  enabled?: boolean;
};

export function useAirportSearchGet({
  airportName = '',
  select = (data) => data,
  enabled = false,
}: AirportSearchType) {
  const isLongEnough: boolean = airportName?.length >= 3;

  return useQuery<AirportSearchViewModel[]>(
    ['airport-search', airportName],
    async () => {
      return await callService('SearchesService', 'searchesFlight', {
        s: airportName,
      });
    },
    {
      keepPreviousData: isLongEnough,
      enabled: isLongEnough && enabled,
      cacheTime: 10000,
      staleTime: 10000,
      select,
    }
  );
}

export const useFindAirportByAirportCode = (
  airportCodeParam: string,
  isCityParam = false,
  enabled = true
) =>
  useAirportSearchGet({
    airportName: airportCodeParam,
    select: (airports) => {
      if (isCityParam) {
        return airports.filter(
          ({ airportCode, isCity }) =>
            airportCode === airportCodeParam && isCity
        );
      }

      return airports.filter(
        ({ airportCode, isCity }) => airportCode === airportCodeParam && !isCity
      );
    },
    enabled,
  });

export function useFlightListGet({
  departure,
  departureIsCity,
  arrival,
  arrivalIsCity,
  departureDate,
  returnDate,
  adt,
  chd,
  inf,
  direct,
  oneWay,
  domestic,
  onSettled = () => false,
}: {
  departure?: string;
  departureIsCity?: boolean;
  arrival?: string;
  arrivalIsCity?: boolean;
  departureDate?: string;
  returnDate?: string;
  adt?: string;
  chd?: string;
  inf?: string;
  direct?: boolean;
  oneWay?: boolean;
  domestic?: boolean;
  onSettled?: () => void;
} = {}) {
  return useQuery<FlightListViewModel>(
    [
      'flights',
      departure,
      departureIsCity,
      arrival,
      arrivalIsCity,
      departureDate,
      returnDate,
      adt,
      chd,
      inf,
      direct,
      oneWay,
      domestic,
    ],
    async () => {
      const response = await callService('FlightsService', 'listFlights', {
        page: 1,
        size: LIST_FLIGHTS_PAGE_SIZE,
        departure,
        departureIsCity,
        arrival,
        arrivalIsCity,
        departureDate,
        returnDate,
        adt,
        chd,
        inf,
        direct,
        oneWay,
        domestic,
      });
      response.data.forEach(({ flights }) => {
        flights.forEach((f) => {
          if (f.provider === 'PGS') {
            f.fareGroup.forEach((fg) => {
              if (!fg?.baggage) {
                fg['baggage'] = f.baggage;
              }
            });
            f.segments.forEach((seg) => {
              if (!seg.type) {
                seg.type = 'type';
              }
            });
          }
        });
      });

      return response;
    },
    {
      keepPreviousData: false,
      cacheTime: 0,
      staleTime: 0,
      enabled:
        !!departure &&
        !!arrival &&
        !!departureDate &&
        !!adt &&
        (oneWay ? true : !!returnDate),
      onSettled,
    }
  );
}

export function useGetFlightRules({
  price,
  currency,
  token,
  flights,
  fareGroupCode,
}: {
  price: number;
  currency: string;
  token: string;
  flights: Array<FlightNodeViewModel>;
  fareGroupCode: string;
}) {
  return useQuery<string>(
    ['flight-rules', token],
    async () => {
      return await callService('FlightsService', 'getRules', {
        token,
        body: { plan: { flights, price, currency, token, tokens: [token] } },
      });
    },
    {
      cacheTime: 0,
      staleTime: 0,
      enabled: !!token && !!fareGroupCode,
    }
  );
}
